import { useState, useEffect, useRef } from 'react';
import {
  Sparkle,
  Clock,
  CircleNotch,
  Brain,
  ArrowsClockwise,
  WarningCircle,
  Check,
} from '@phosphor-icons/react';
import { getAccessToken } from '@/lib/tokenStorage';
import { Button } from '../../../components/ui/Button';
import { SourceBadges } from './SourceBadges';
import { explainEnrichmentRunError } from '@/lib/enrichmentError';
import { api } from '@/lib/api';
import { pollEnrichmentUntilTerminal } from '@/lib/enrichmentPoll';

interface EnrichmentPanelProps {
  contactId: number;
  enrichmentData: any;
  enrichmentStatus: string | null;
  onEnrichmentComplete: (data: any) => void;
  enrichmentResults?: Record<string, any>;
  onOpenCatalog?: () => void;
  /** Contact flagged invalid/fake_email — enrichment is blocked (bug 21670). */
  undeliverable?: boolean;
}

// Shown when a contact's email is flagged invalid/fake_email. Enriching,
// researching, or re-enriching it would burn credits on a dead address, so the
// backend refuses it — reflect that here by disabling the action.
const UNDELIVERABLE_HINT =
  "This contact's email is flagged invalid. Enrichment is disabled to avoid wasting credits.";

// Copy for a stream that never opened. Only claim what the status code actually
// proves: 401/403 is something the user can fix (expired session or rejected
// provider credentials), 5xx is ours. Any other status usually carries a
// run-level reason in FastAPI's `detail`, which explainEnrichmentRunError
// already knows how to phrase — reuse it rather than inventing more copy.
function describeStreamHttpFailure(status: number, detail?: string | null): string {
  if (status === 401 || status === 403) {
    return 'Your session or provider credentials were rejected, so live research updates stopped. Sign in again, or check your keys under Settings > Integrations.';
  }
  if (status >= 500) {
    return `The server stopped sending research updates (HTTP ${status}). Live progress isn't available for this run.`;
  }
  return detail?.trim()
    ? explainEnrichmentRunError(detail)
    : `Live research updates stopped unexpectedly (HTTP ${status}).`;
}

export function EnrichmentPanel({
  contactId,
  enrichmentData,
  enrichmentStatus,
  onEnrichmentComplete,
  enrichmentResults,
  onOpenCatalog,
  undeliverable = false,
}: EnrichmentPanelProps) {
  const [progressSteps, setProgressSteps] = useState<Array<{ step: string; label: string }>>([]);
  // One display-ready failure message for the whole stream. All three ways a run
  // can die on us — an `error` event, a non-2xx response, a dropped connection —
  // collapse into this single terminal state because the UI answer is identical:
  // stop the spinner, say what we know, offer Retry. It has to live here because
  // `enrichmentStatus` is a prop, so only the parent can leave 'in_progress' —
  // without local state this surface has no way to stop claiming progress.
  const [streamFailure, setStreamFailure] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // The stream and the poll below race to end the run. Whichever gets there
  // first wins; this stops the loser acting on a run already reported.
  const settledRef = useRef(false);

  // Connect to SSE stream when status is in_progress
  useEffect(() => {
    if (enrichmentStatus !== 'in_progress') {
      setProgressSteps([]);
      setStreamFailure(null);
      return;
    }

    // A fresh run must not open onto the previous run's error or its half-built
    // step trail — Retry reuses this same component instance.
    setStreamFailure(null);
    setProgressSteps(prev => (prev.length ? [] : prev));

    const controller = new AbortController();
    abortRef.current = controller;
    settledRef.current = false;

    // Safety net beside the stream. The SSE connection is the only thing that
    // can end this spinner, and it is one dropped/buffered response away from
    // never doing so -- the panel has no timeout of its own. The server already
    // runs Redis pub/sub AND a DB poll internally for the same reason; this is
    // that idea on the client. SSE still normally wins and still carries the
    // live step trail, so a healthy run is unchanged apart from one small GET
    // every few seconds while it is genuinely in progress.
    const stopPolling = pollEnrichmentUntilTerminal({
      fetchStatus: async () => {
        const res = await api.get(`/enrichment/${contactId}/status`);
        return res.data;
      },
      onTerminal: snapshot => {
        if (settledRef.current) return;
        settledRef.current = true;
        controller.abort();
        if (snapshot.status === 'completed') {
          onEnrichmentComplete(snapshot.enrichment_data ?? null);
          setProgressSteps([]);
        } else {
          setStreamFailure(
            explainEnrichmentRunError('Enrichment failed'),
          );
        }
      },
    });

    const connectStream = async () => {
      try {
        const token = getAccessToken();
        const baseUrl = import.meta.env.VITE_API_URL || '/api';
        const response = await fetch(`${baseUrl}/enrichment/${contactId}/stream`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          // Returning quietly here is what used to strand the spinner forever, so
          // salvage everything knowable — status plus whatever the body says —
          // and hand it to the terminal state.
          let detail: string | null = null;
          try {
            const body = (await response.text()).trim();
            if (body) {
              // FastAPI errors are `{"detail": "..."}`; keep anything else verbatim.
              let parsed: any = null;
              try { parsed = JSON.parse(body); } catch { /* not JSON */ }
              detail = typeof parsed?.detail === 'string' ? parsed.detail : body;
            }
          } catch { /* body missing or unreadable — the status alone still tells a story */ }
          setStreamFailure(describeStreamHttpFailure(response.status, detail));
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'step') {
                setProgressSteps(prev => {
                  if (data.step === 'waiting' && prev.some(s => s.step === 'waiting')) return prev;
                  return [...prev, { step: data.step, label: data.label }];
                });
              } else if (data.type === 'complete') {
                if (settledRef.current) return;
                settledRef.current = true;
                onEnrichmentComplete(data.enrichment_data);
                setProgressSteps([]);
                return;
              } else if (data.type === 'error') {
                if (settledRef.current) return;
                settledRef.current = true;
                // Shape at write time so every setStreamFailure caller stores
                // display-ready copy and the render stays dumb.
                setStreamFailure(explainEnrichmentRunError(data.error));
                return;
              }
            } catch { /* ignore parse errors */ }
          }
        }
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          // Network drop, proxy timeout, server restart. We genuinely can't know
          // whether the backend run survived, so the copy doesn't claim it died —
          // but this surface can no longer observe it either, and a spinner that
          // will never resolve is worse than an honest dead end plus a Retry.
          setStreamFailure(
            'Live research updates stopped — the connection to the research stream was lost. Reopen this contact to check whether the run finished, or retry the research.',
          );
        }
      }
    };

    connectStream();

    return () => {
      stopPolling();
      controller.abort();
      abortRef.current = null;
    };
  }, [enrichmentStatus, contactId]);

  // Not enriched yet
  if (!enrichmentData && enrichmentStatus !== 'in_progress') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-emphasis tracking-tight text-foreground flex items-center gap-2">
            <Brain className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            AI Enrichment
          </h3>
        </div>
        <div className="border border-dashed border-border/60 rounded-xl p-8 text-center">
          <div className="h-14 w-14 rounded-2xl bg-muted/70 flex items-center justify-center mx-auto mb-3">
            <Sparkle className="h-7 w-7 text-[#5e6ad2] dark:text-[#a5b4fc]" />
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {undeliverable
              ? "This contact's email is flagged invalid, so enrichment is disabled."
              : 'Use AI to research this contact — get outreach hooks, buying signals, competitive intel, and more.'}
          </p>
          <Button
            variant="default"
            icon={<Sparkle className="h-4 w-4" />}
            onClick={() => onOpenCatalog?.()}
            disabled={undeliverable}
            title={undeliverable ? UNDELIVERABLE_HINT : undefined}
          >
            Research Contact
          </Button>
        </div>
      </div>
    );
  }

  // In progress — with live SSE streaming
  if (enrichmentStatus === 'in_progress') {
    const completedSteps = progressSteps.filter(s => s.step !== 'waiting');

    // The stream is dead but the parent still says 'in_progress', so this branch
    // owns the terminal state. Same chrome as the `failed` branch below: the two
    // error surfaces are the same thing to the user and should read as one.
    if (streamFailure) {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-emphasis tracking-tight text-foreground flex items-center gap-2">
            <Brain className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            AI Enrichment
          </h3>
          <div role="alert" className="border border-red-500/20 rounded-xl p-6 text-center">
            <div className="h-14 w-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-3">
              <WarningCircle className="h-7 w-7 text-red-600 dark:text-red-400" />
            </div>
            <p className="text-sm text-red-600 dark:text-red-400 mb-1">Live updates stopped.</p>
            <p className="text-xs text-muted-foreground mb-4">{streamFailure}</p>
            {/* Keep the trail we did collect — "we got this far" is more honest
                and more useful than discarding the partial progress. */}
            {completedSteps.length > 0 && (
              <div className="text-left space-y-1.5 mb-4">
                {completedSteps.map((ps, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs">
                    <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                    <span className="text-muted-foreground">{ps.label}</span>
                  </div>
                ))}
              </div>
            )}
            <Button
              variant="outline"
              icon={<ArrowsClockwise className="h-4 w-4" />}
              onClick={() => onOpenCatalog?.()}
              disabled={undeliverable}
              title={undeliverable ? UNDELIVERABLE_HINT : undefined}
            >
              Retry
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-emphasis tracking-tight text-foreground flex items-center gap-2">
          <Brain className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
          AI Enrichment
        </h3>
        {/* Steps arrive asynchronously over SSE, so announce them politely rather
            than leaving screen readers on a silent, unchanging panel. */}
        <div
          role="status"
          aria-live="polite"
          className="border border-border/60 rounded-xl p-4 max-h-[300px] overflow-y-auto"
        >
          <div className="flex items-center gap-3 mb-3">
            <CircleNotch className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc] animate-spin flex-shrink-0" />
            <p className="text-sm font-medium text-foreground">Research in progress...</p>
          </div>
          {progressSteps.length > 0 && (
            <div className="space-y-1.5 ml-1">
              {completedSteps.map((ps, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                  <span className="text-muted-foreground">{ps.label}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-xs animate-pulse">
                <CircleNotch className="h-3 w-3 text-[#5e6ad2] dark:text-[#a5b4fc] animate-spin flex-shrink-0" />
                <span className="text-[#5e6ad2] dark:text-[#a5b4fc] font-medium">
                  {progressSteps.some(s => s.step === 'waiting') ? 'Finalizing research...' : 'Processing...'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Failed
  if (enrichmentStatus === 'failed' && !enrichmentData) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-emphasis tracking-tight text-foreground flex items-center gap-2">
          <Brain className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
          AI Enrichment
        </h3>
        <div className="border border-red-500/20 rounded-xl p-6 text-center">
          <div className="h-14 w-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-3">
            <WarningCircle className="h-7 w-7 text-red-500" />
          </div>
          <p className="text-sm text-red-500 mb-1">Research couldn't be completed.</p>
          <p className="text-xs text-muted-foreground mb-4">
            Here's what happened with each provider:
          </p>
          {enrichmentResults && Object.keys(enrichmentResults).length > 0 ? (
            <div className="text-left mb-4">
              <SourceBadges results={enrichmentResults} />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground mb-4">
              No provider returned data. Please try again, or check your provider
              API keys under Settings &gt; Integrations.
            </p>
          )}
          <Button
            variant="outline"
            icon={<ArrowsClockwise className="h-4 w-4" />}
            onClick={() => onOpenCatalog?.()}
            disabled={undeliverable}
            title={undeliverable ? UNDELIVERABLE_HINT : undefined}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Enriched — show controls
  const metadata = enrichmentData?.metadata || {};
  const depthLabel = metadata.depth
    ? { quick: 'Quick Lookup', standard: 'Standard Research', deep: 'Deep Research' }[metadata.depth as string] || 'AI Research'
    : 'AI Research';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-emphasis tracking-tight text-foreground flex items-center gap-2">
            <Brain className="h-5 w-5 text-[#5e6ad2] dark:text-[#a5b4fc]" />
            AI Enrichment
          </h3>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1.5 ml-7">
            <span className="font-medium text-foreground/80">{depthLabel}</span>
            {metadata.enriched_at && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {new Date(metadata.enriched_at).toLocaleDateString()}
              </span>
            )}
            {metadata.processing_time_seconds && (
              <span>{Math.round(metadata.processing_time_seconds)}s</span>
            )}
            {metadata.sources_used != null && metadata.sources_used > 0 && (
              <span>{metadata.sources_used} sources</span>
            )}
            {metadata.cost_usd != null && (
              <span className="text-muted-foreground/70">${metadata.cost_usd < 0.01 ? '<0.01' : metadata.cost_usd.toFixed(2)}</span>
            )}
          </div>
          {enrichmentResults && Object.keys(enrichmentResults).length > 0 && (
            <SourceBadges results={enrichmentResults} />
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          icon={<ArrowsClockwise className="h-3.5 w-3.5" />}
          onClick={() => onOpenCatalog?.()}
          disabled={undeliverable}
          title={undeliverable ? UNDELIVERABLE_HINT : undefined}
        >
          Re-enrich
        </Button>
      </div>
    </div>
  );
}
